import { Injectable } from '@angular/core';
import { AppSettings } from 'app/app.settings';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppinfoService } from './appinfo.service';
import { CommonRequestService, PagingParam } from './common-request.service';
import { LayerQcService } from './layer-qc.service';
import { FilterOption, ModelMenuItemKey } from './main-data.service';
import { UserService } from './user.service';

import { v1 as uuidv1 } from 'uuid';
import { SocketLogs, SocketService } from './socket.service';

export interface AppInterlayerReport {
	label: string;
	subjectLayerCate: string;
	// subjectCateShort: string;
	refLayerCate: string;
	// refCateShort: string;
	subjectLayerId: number;
	refLayerId: number;
	type: InterlayerReportType
}
export type InterlayerReportType = 'FISHBONE' | 'OVERLAY' | 'BORDER' | 'ALL';

export interface ReportQaNote {
	cd: string;
	msg: string;
}

@Injectable({
  providedIn: 'root'
})
export class InterlayerReportService {
	selectedReportRecordsBS: BehaviorSubject<{
		rec;
		source?: 'table' | 'map' | 'card'
	}> = new BehaviorSubject(null);

	selectedReportBS: BehaviorSubject<AppInterlayerReport> = new BehaviorSubject(null);
		// // This selectedReportTypeInDropdownBS is the value in report type dropdown list. It can be different than selectedReport.type when the value is ALL
		// selectedReportTypeInDropdownBS: BehaviorSubject<InterlayerReportType> = new BehaviorSubject(null);
	
	reportListBS: BehaviorSubject<{
		reportType: InterlayerReportType; // It can be different than selectedReport.type when the reportType is ALL
		list: AppInterlayerReport[];
	}> = new BehaviorSubject(null);

	// NOTE: reportTypeBS is a RESULT of 1.selectedReportBS change 2.reportListBS change
	reportTypeBS: BehaviorSubject<{
		type: InterlayerReportType
	}> = new BehaviorSubject(null);

	reportFilterInfoBS: BehaviorSubject<{
		filterOptions: FilterOption[];
		source?: 'filterTabToggle' | 'code'
	}> = new BehaviorSubject({
		filterOptions: []
	});

	requestData: Subject<{
		layerChange?: boolean;
	}> = new Subject();

	rows: any[];

	// Qc messages by processId
	qrLogs: BehaviorSubject<SocketLogs> = new BehaviorSubject({});
	onQrFinish: Subject<{ isError: boolean;}> = new Subject();
	constructor(
		private appinfoService: AppinfoService,
		private requestService: CommonRequestService,
		private qcService: LayerQcService,
		private socketService: SocketService,
		private userService: UserService
	) { }

	// Get report data (other than FISHBONE)
	getQrOther(layerIdSbj, layerIdRef, qaCodes: string[], bbox?: number[], paging?: PagingParam) {
		return new Promise<any>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'azgiv/qrOther';
			const params: any = {
				layerIdSbj: layerIdSbj,
				layerIdRef: layerIdRef
			};
			if (qaCodes) {
				params.qaCodes = JSON.stringify(qaCodes);
			}
			if (bbox) {
				params.bbox = JSON.stringify(bbox);
			}
			if (paging) {
				this.requestService.setParamsPaging(params, paging);
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				resolve(rsp);
			}, async err => reject(err));
		});
	}
	getQrOverlay(layerIdSbj, layerIdRef, qaCodes: string[], bbox?: number[], paging?: PagingParam) {
		return new Promise<any>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'azgiv/qrOverlay';
			const params: any = {
				layerIdSbj: layerIdSbj,
				layerIdRef: layerIdRef
			};
			if (qaCodes) {
				params.qaCodes = JSON.stringify(qaCodes);
			}
			if (bbox) {
				params.bbox = JSON.stringify(bbox);
			}
			if (paging) {
				this.requestService.setParamsPaging(params, paging);
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				resolve(rsp);
			}, async err => reject(err));
		});
	}
	// Generate
	async qrInterlayer(reportType: InterlayerReportType, model?: ModelMenuItemKey, editFilter?) {
		const url = AppSettings.serviceRootUrl + 'azgiv/qrInterlayer';
		const userInfo = this.userService.getUserInfo();
		const body: any = {
				userSubscriptionId: userInfo.userSubId,
				sessionId: this.appinfoService.getSessionUuid(),
				qrFishbone: reportType === 'ALL' || reportType === 'FISHBONE',
				qrOverlay: reportType === 'ALL' || reportType === 'OVERLAY'
		};
		if (model) {
			body.model = model;
		}
		if (editFilter) {
			body.editFilter = JSON.stringify(editFilter);
		}
		
		const tempProcessId = uuidv1();
		this.socketService.onInitProcess.next({
			tempId: tempProcessId,
			processType: 'QR'
		});
		return await new Promise<{processId;}>((resolve, reject) => {
			this.requestService.requestPost(url, body, (rsp) => {
				const processesById = this.qrLogs.getValue();
				const processId = rsp.data.content;
				this.socketService.onInitProcess.next({
					tempId: tempProcessId,
					processId: processId,
					processType: 'QR'
				});
				const process = this.socketService.registerProcessById(rsp.data.content, processesById);
				this.qrLogs.next(processesById);
				resolve({
					processId: processId
				});
			}, err => reject(err));
		});
	}

	getQrReportTabs(reportType?: InterlayerReportType) {
		return new Promise<AppInterlayerReport[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'azgiv/qrReportTabs';
			const params: any = {
				customerId: this.userService.getCustomerIdByType()
			};
			if (reportType) {
				params.gqrType = reportType;
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				const reports: AppInterlayerReport[] = [];
				if (rsp.data) {
					rsp.data.forEach(i => {
						const subjectLayerCateCode = Object.keys(i.sbj_layer)[0];
						const subjectLayerId = i.sbj_layer[subjectLayerCateCode];
						const subjectLayerCate = this.appinfoService.layerTypeList.find(t => t.code === subjectLayerCateCode);
						i.ref_layers.forEach(ref => {
							const refLayerCateCode = Object.keys(ref)[0];
							const refLayerId = ref[refLayerCateCode];
							const refLayerCate = this.appinfoService.layerTypeList.find(t => t.code === refLayerCateCode);
							const report: AppInterlayerReport = {
								subjectLayerCate: subjectLayerCate.code,
								// subjectCateShort: subjectLayerCate.nameShort,
								subjectLayerId: subjectLayerId,
								refLayerCate: refLayerCate.code,
								// refCateShort: refLayerCate.nameShort,
								refLayerId: refLayerId,
								label: `${subjectLayerCate.nameShort} - ${refLayerCate.nameShort}`,
								type: i.gqr_type
							};
							reports.push(report);
						});
					})
				}
				resolve(reports);
			}, async err => reject(err));
		});
	}
	async updateQrFilterOptions(reportType: InterlayerReportType, layerId1, layerId2) {
		let filterOptions: FilterOption[];
		if (!reportType || !layerId1 || !layerId2) {
			filterOptions = [];
		} else {
			filterOptions = await this.getQrFilterOptions(reportType, layerId1, layerId2);
			filterOptions.forEach(i => i.isSelected = !!i.count); // Default select all that has count
		}
		this.reportFilterInfoBS.next({
			filterOptions: filterOptions
		});
	}
	private async getQrFilterOptions(reportType: InterlayerReportType, layerId1, layerId2) {
		const cusId = this.userService.getCustomerIdByType();
		const all: FilterOption[] = [];
		const allQaRules = await this.qcService.getQaRules(cusId, reportType, null, layerId1, layerId2, 'code');
		allQaRules.forEach((d) => {
			const obj = {
				name: d.ruleName,
				value: d.code,
				count: d.count,
				descriptionLong: d.describLong,
				errorLevel: d.errorLevel,
				// isSelected: isSelected, // TODO
				type: d.type
			};
			all.push(obj);
		});
		// Put the ones with count in the front
		all.sort(this.qcService.sortFilterByCount);
		return all;
	}
}
